import { useCallback } from "react";
import { atom, PrimitiveAtom, useAtom } from "jotai";

import { Api } from "interfaces";

import * as flowConfig from "flows/eta-estimator/config";

import { useGetTypesForFile } from "modules/data-files/api";
import { useCreateProjectWithDataAndWorkspace } from "modules/projects/api";

import { useGetInputFile } from "./params";
import {
  useGetOriginLatColumn,
  useGetOriginLongColumn,
  useGetDestinationLatColumn,
  useGetDestinationLongColumn,
} from "./params";
import { useGetStartTimeColumn, useGetEndTimeColumn } from "./params";

const projectCreateStatus = atom<"not-started" | "in-progress" | "completed">(
  "not-started"
);

const projectCreateError = atom<string | Api.ApiError>(null) as PrimitiveAtom<
  string | Api.ApiError
>;

export const useGetCreate = () => {
  const createProject = useCreateProjectWithDataAndWorkspace();
  const inputFile = useGetInputFile();

  const colTypes = useGetTypesForFile(inputFile);

  const originLat = useGetOriginLatColumn();
  const originLong = useGetOriginLongColumn();
  const destLat = useGetDestinationLatColumn();
  const destLong = useGetDestinationLongColumn();

  const startTime = useGetStartTimeColumn();
  const endTime = useGetEndTimeColumn();

  const [status, setStatus] = useAtom(projectCreateStatus);
  const [error, setError] = useAtom(projectCreateError);

  const create = useCallback(async () => {
    if (
      !inputFile ||
      !originLat ||
      !originLong ||
      !destLat ||
      !destLong ||
      !startTime ||
      !endTime
    ) {
      setError("Input Data, Lat/Longs and time columns ARE ALL MANDATORY!!!");
      return;
    }
    setStatus("in-progress");
    setError(null);
    const createSpec = {
      flow: flowConfig.FLOW_NAME,
      data_files: { [flowConfig.INPUT_DATA]: inputFile },
      meta_data: {
        [flowConfig.ORIGIN_LAT_KEY]: originLat,
        [flowConfig.ORIGIN_LONG_KEY]: originLong,
        [flowConfig.DEST_LAT_KEY]: destLat,
        [flowConfig.DEST_LONG_KEY]: destLong,
        [flowConfig.START_TIME_KEY]: startTime,
        [flowConfig.END_TIME_KEY]: endTime,
      },
    };
    const augmentSpec = {
      key: flowConfig.AUGMENTED_FILE,
      output_file_name: `${inputFile}-with-duration`,
      source_data: inputFile,
      create_type: "AUGMENT",
      create_spec: {
        new_columns: [
          {
            name: flowConfig.Y_NAME,
            type: "arithmetic",
            details: {
              col1_name: endTime,
              col2_name: startTime,
              operation: "-",
            },
          },
        ],
        columns_to_drop: [endTime],
        column_types: colTypes,
        missing_treatment: [
          originLat,
          originLong,
          destLat,
          destLong,
          startTime,
          endTime,
        ].map((column) => ({
          column,
          treatment: "drop-rows",
        })),
      },
    };
    const resp = await createProject({
      "project-spec": createSpec,
      // @ts-ignore
      "data-spec": augmentSpec,
    });
    console.log(resp);
    if (resp.success) {
    } else if (resp.success === false) {
      setError(resp.error);
    }
    setStatus("completed");
    return resp;
  }, [
    inputFile,
    originLat,
    originLong,
    destLat,
    destLong,
    startTime,
    endTime,
    colTypes,
  ]);

  return [create, status, error] as const;
};
